<template>
  <div class="p-results-check-tasks">
    <div
      v-if="taskCheckKeywords.from && taskCheckKeywords.to"
      class="p-results-check-tasks__date"
    >
      <IconDefault type="calendar" :size="16" />
      <p>
        {{ taskCheckKeywords.from | dateText('YYYY.MM.DD') }} -
        {{ taskCheckKeywords.to | dateText('YYYY.MM.DD') }}
      </p>
      <Tooltip size="sm">
        現時点の3日前から過去1ヶ月間のデータを表示します。
      </Tooltip>
    </div>
    <ListActionResult
      :checked="params.checked"
      :items="sortedItems"
      :sort="params.sort"
      :usage="usage"
      :isLoading="taskCheckKeywords.isLoading"
      @check="updateChecked"
      @sort="updateSort"
      @openModalAddKeyword="openModalAddKeyword"
    />
    <portal to="form-add-tasks">
      <transition name="v-fade">
        <FormAddTasks
          v-if="checkedCount"
          :project="project"
          defaultType="rewrite"
          gaType="ua"
          :items="checkedItems"
          @reset="resetChecked"
          key="form"
        />
      </transition>
    </portal>
  </div>
</template>

<script>
import FormAddTasks from '@/components/siteImportantKeywordGA4/FormAddTasks';
import IconDefault from '@/components/icon/IconDefault';
import ListActionResult from '@/components/siteImportantKeyword/ListActionResult';
import Tooltip from '@/components/TooltipPortal';
import {
  getProjectObservationKeywords,
  getProjectObservationKeywordSummaries
} from '@/api/v2/projects';
import utils from '@/utils';
import axios from 'axios';

export default {
  props: {
    device: {
      type: String,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    usage: {
      type: Object,
      required: true
    }
  },
  components: {
    FormAddTasks,
    IconDefault,
    ListActionResult,
    Tooltip
  },
  data() {
    return {
      cancelToken: {
        taskCheckKeywords: null,
        taskCheckSummaries: {}
      },
      taskCheckKeywords: {
        from: null,
        to: null,
        items: [],
        isLoading: true,
        summaries: {}
      },
      taskCheckSummaries: {
        items: {}
      },
      params: {
        checked: [],
        sort: {
          name: 'doneDate',
          orderby: 'desc',
          type: 'date'
        }
      }
    };
  },
  computed: {
    checkedCount() {
      return this.checkedItems.length;
    },
    checkedItems() {
      const filteredItems = this.taskCheckKeywords.items.filter(item =>
        this.params.checked.includes(item.id)
      );
      return filteredItems.map(item => {
        const taskCheckSummariesItem = this.taskCheckSummaries.items[item.id];
        if (taskCheckSummariesItem) {
          return {
            ...item,
            ...taskCheckSummariesItem
          };
        }
        return {
          ...item
        };
      });
    },
    sortedItems() {
      return this.sort(this.customedItems.slice(), this.params.sort);
    },
    customedItems() {
      const items = this.taskCheckKeywords.items;
      const summariesItems = this.taskCheckSummaries.items;
      return items.map(item => {
        let summariesItem = summariesItems[item.id];
        if (!summariesItem) {
          summariesItem = {
            clicks: null,
            conversions: null,
            ctr: null,
            impressions: null,
            searchVolumes: null,
            status: null,
            isLoading: true
          };
        }
        let rankDiff = null;
        let doneTaskRank = null;
        if (item.doneTask.type !== 'createContent') {
          doneTaskRank = item.doneTaskRank;
        }
        if (item.currentRank && doneTaskRank) {
          rankDiff = doneTaskRank - item.currentRank;
        }
        return {
          ...summariesItem,
          currentRank: item.currentRank,
          doneDate: item.doneTask.date,
          type: item.doneTask.type,
          isProposal: item.doneTask.isProposal,
          doneTaskRank,
          rankDiff,
          id: item.id,
          keyword: item.keyword,
          title: item.title,
          url: item.url
        };
      });
    }
  },
  watch: {
    device: {
      handler() {
        this.getTaskCheckKeywords();
      }
    }
  },
  created() {
    this.getTaskCheckKeywords();
  },
  beforeDestroy() {
    this.cancelRequests();
  },
  methods: {
    cancelRequests() {
      if (this.cancelToken.taskCheckKeywords) {
        this.cancelToken.taskCheckKeywords.cancel();
      }
      Object.keys(this.cancelToken.taskCheckSummaries).forEach(key => {
        this.cancelToken.taskCheckSummaries[key].cancel();
      });
    },
    getTaskCheckKeywords() {
      this.taskCheckKeywords.isLoading = true;

      // 初期化
      this.cancelRequests();
      this.taskCheckKeywords.from = null;
      this.taskCheckKeywords.to = null;
      this.taskCheckKeywords.items = [];
      this.taskCheckSummaries.items = {};

      const params = {
        device: this.device
      };
      this.cancelToken.taskCheckKeywords = axios.CancelToken.source();

      getProjectObservationKeywords({
        cancelToken: this.cancelToken.taskCheckKeywords.token,
        idToken: this.auth.idToken,
        params,
        projectId: this.project.id
      })
        .then(response => {
          // データがない時は204でレスポンスが空で返ってくる
          if (response.status === 200) {
            this.taskCheckKeywords.from = response.data.from;
            this.taskCheckKeywords.to = response.data.to;
            this.taskCheckKeywords.items = response.data.items;
          }
          this.taskCheckKeywords.isLoading = false;
          this.getAllTaskCheckSummaries();
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.taskCheckKeywords.isLoading = false;
          }
        });
    },
    async getAllTaskCheckSummaries() {
      const allItems = this.taskCheckKeywords.items;
      const chunkSize = 20;
      const splitLength = Math.ceil(allItems.length / chunkSize);

      for (let splitIndex = 0; splitIndex < splitLength; splitIndex++) {
        // 既にコンポーネントが破棄されていたら終了する
        if (this._isDestroyed) {
          break;
        }
        const splitItems = allItems.slice(
          splitIndex * chunkSize,
          (splitIndex + 1) * chunkSize
        );
        await Promise.all(
          splitItems.map(item => {
            return this.getTaskCheckSummaries(item.id);
          })
        );
      }
    },
    getTaskCheckSummaries(keywordId) {
      if (this.taskCheckSummaries.items[keywordId]) {
        return;
      }
      this.taskCheckSummaries.items[keywordId] = { isLoading: true };

      const params = {
        device: this.device,
        from: this.taskCheckKeywords.from,
        to: this.taskCheckKeywords.to
      };
      this.cancelToken.taskCheckSummaries[
        keywordId
      ] = axios.CancelToken.source();

      return getProjectObservationKeywordSummaries({
        cancelToken: this.cancelToken.taskCheckSummaries[keywordId].token,
        idToken: this.auth.idToken,
        params,
        keywordId: keywordId,
        projectId: this.project.id
      })
        .then(response => {
          let data = {};
          // データがない時は204でレスポンスが空で返ってくる
          if (response.status === 200) {
            data = response.data;
          }
          this.taskCheckSummaries.items = Object.assign(
            {},
            this.taskCheckSummaries.items,
            {
              ...this.taskCheckSummaries.items,
              [keywordId]: {
                ...response.data,
                status: response.status,
                isLoading: false
              }
            }
          );
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.taskCheckSummaries.items = Object.assign(
              {},
              this.taskCheckSummaries.items,
              {
                ...this.taskCheckSummaries.items,
                [keywordId]: {
                  status: error.response.status,
                  isLoading: false
                }
              }
            );
          }
        });
    },
    updateChecked(checked) {
      this.params.checked = checked;
    },
    updateSort(sort) {
      this.params.sort = sort;
    },
    resetChecked() {
      this.params.checked = [];
    },
    sort(items, params) {
      if (params.type === 'date') {
        return this.sortDate(items, params.orderby, params.name);
      } else if (params.type === 'number') {
        return this.sortNumber(items, params.orderby, params.name);
      } else if (params.type === 'rank') {
        return this.sortNumber(items, params.orderby, params.name, true);
      } else {
        return this.sortString(items, params.orderby, params.name);
      }
    },
    sortDate(items, orderby = 'asc', key) {
      return items.sort((a, b) => {
        let date1 = key ? a[key] : a;
        let date2 = key ? b[key] : b;
        if (date1 === date2) {
          return this.compareString(a, b, orderby, 'keyword');
        } else if (!date1) {
          return 1;
        } else if (!date2) {
          return -1;
        }
        let time1 = utils.getDateResetHours(date1).getTime();
        let time2 = utils.getDateResetHours(date2).getTime();
        if (time1 === time2) {
          return this.compareString(a, b, orderby, 'keyword');
        } else if (orderby === 'asc') {
          return time1 - time2;
        } else {
          return time2 - time1;
        }
      });
    },
    sortNumber(items, orderby = 'asc', key, isNullSort = false) {
      return items.sort((a, b) => {
        let num1 = key ? a[key] : a;
        let num2 = key ? b[key] : b;
        if (num1 === num2) {
          return this.compareString(a, b, orderby, 'keyword');
        } else if (num1 === null || typeof num1 === 'undefined') {
          // nullをソートに含める場合、nullを最大値的な扱いをする
          if (isNullSort && orderby !== 'asc') {
            return -1;
          }
          return 1;
        } else if (num2 === null || typeof num2 === 'undefined') {
          // nullをソートに含める場合、nullを最大値的な扱いをする
          if (isNullSort && orderby !== 'asc') {
            return 1;
          }
          return -1;
        }
        if (orderby === 'asc') {
          return num1 - num2;
        } else {
          return num2 - num1;
        }
      });
    },
    sortString(items, orderby = 'asc', key) {
      return items.sort((a, b) => {
        let str1 = utils.convertToSearchString(key ? a[key] : a);
        let str2 = utils.convertToSearchString(key ? b[key] : b);
        if (str1 === str2) {
          return this.compareString(a, b, orderby, 'keyword');
        } else if (!str1) {
          return 1;
        } else if (!str2) {
          return -1;
        }
        if (str1 > str2) {
          if (orderby === 'asc') {
            return 1;
          } else {
            return -1;
          }
        } else if (str1 < str2) {
          if (orderby === 'asc') {
            return -1;
          } else {
            return 1;
          }
        }
      });
    },
    compareString(a, b, orderby = 'asc', key) {
      let str1 = utils.convertToSearchString(key ? a[key] : a);
      let str2 = utils.convertToSearchString(key ? b[key] : b);
      if (str1 === str2) {
        return 0;
      } else if (!str1) {
        return 1;
      } else if (!str2) {
        return -1;
      }
      if (str1 > str2) {
        if (orderby === 'asc') {
          return 1;
        } else {
          return -1;
        }
      } else if (str1 < str2) {
        if (orderby === 'asc') {
          return -1;
        } else {
          return 1;
        }
      }
    },
    openModalAddKeyword() {
      this.$emit('openModalAddKeyword');
    }
  }
};
</script>
